import { DEFAULT_NODE_CONFIGS } from '../constants';
import { IconType, NodeType } from '../types';
import type { AppState, SimulationConfig } from '../types';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const nodeTypes = new Set<string>(Object.values(NodeType));
const iconTypes = new Set<string>(Object.values(IconType));

const isSimulationConfig = (value: unknown): value is SimulationConfig => {
  if (!isRecord(value)) return false;
  const numericKeys: Array<keyof SimulationConfig> = [
    'labProcessingTime', 'admissionRate', 'dischargeProcessTime', 'imagingRate',
    'basePatientSpeed', 'baseStaffSpeed', 'patienceDepletionRate',
    'infectionProbability', 'infectionRadius', 'nodeContaminationRate', 'congestionAwareness'
  ];
  const booleanKeys: Array<keyof SimulationConfig> = [
    'avoidContamination', 'allowHallwayWaiting', 'skipTriageForCritical',
    'strictIsolationProtocol', 'diagnosticsFirst', 'fastTrackTrauma'
  ];
  return numericKeys.every(key => isFiniteNumber(value[key])) &&
    booleanKeys.every(key => typeof value[key] === 'boolean') &&
    (value.staffPrioritization === 'ACUITY' || value.staffPrioritization === 'FIFO');
};

export const normalizeLayoutPayload = (value: unknown): unknown => {
  if (!isRecord(value)) return value;

  const zones = Array.isArray(value.zones)
    ? value.zones.map(zone => {
        if (!isRecord(zone) || Array.isArray(zone.rects)) return zone;
        if (![zone.x, zone.y, zone.w, zone.h].every(isFiniteNumber)) return zone;
        return {
          ...zone,
          rects: [{ x: zone.x, y: zone.y, w: zone.w, h: zone.h }]
        };
      })
    : value.zones;

  const nodes = Array.isArray(value.nodes)
    ? value.nodes.map(node => {
        if (!isRecord(node) || typeof node.type !== 'string' || !nodeTypes.has(node.type)) return node;
        const defaults = DEFAULT_NODE_CONFIGS[node.type as NodeType];
        return {
          ...node,
          connections: Array.isArray(node.connections) ? node.connections : [],
          occupants: Array.isArray(node.occupants) ? node.occupants : [],
          contamination: isFiniteNumber(node.contamination) ? node.contamination : 0,
          capacity: isFiniteNumber(node.capacity) ? node.capacity : defaults.capacity,
          processingTime: isFiniteNumber(node.processingTime) ? node.processingTime : defaults.processingTime,
          speedModifier: isFiniteNumber(node.speedModifier) ? node.speedModifier : defaults.speedModifier
        };
      })
    : value.nodes;

  const normalizedNodes = Array.isArray(nodes) ? nodes.filter(isRecord) : [];
  const paths = Array.isArray(value.paths)
    ? value.paths.map(path => {
        if (!isRecord(path)) return path;
        const points = Array.isArray(path.points) ? path.points : [];
        const kind = path.kind === 'SIMULATION' || path.kind === 'ARCHITECTURAL'
          ? path.kind
          : (typeof path.name === 'string' && path.name.toLowerCase().startsWith('sim.') ? 'SIMULATION' : 'ARCHITECTURAL');
        const inferredNodeIds = points.map(point => {
          if (!isRecord(point)) return null;
          const node = normalizedNodes.find(candidate => candidate.x === point.x && candidate.y === point.y);
          return typeof node?.id === 'string' ? node.id : null;
        });
        const nodeIds = Array.isArray(path.nodeIds) && path.nodeIds.length === points.length
          ? path.nodeIds
          : inferredNodeIds;
        const inferredConnections = kind === 'SIMULATION'
          ? nodeIds.slice(1).flatMap((nodeId, index) => {
              const previousId = nodeIds[index];
              return typeof previousId === 'string' && typeof nodeId === 'string' && previousId !== nodeId
                ? [{ fromId: previousId, toId: nodeId }]
                : [];
            })
          : [];
        return {
          ...path,
          kind,
          nodeIds,
          generatedNodeIds: Array.isArray(path.generatedNodeIds)
            ? path.generatedNodeIds
            : (kind === 'SIMULATION' ? normalizedNodes.filter(node => node.type === NodeType.WAYPOINT && nodeIds.includes(node.id)).map(node => node.id) : []),
          connections: Array.isArray(path.connections) ? path.connections : inferredConnections,
          color: typeof path.color === 'string' ? path.color : '#06b6d4',
          visible: typeof path.visible === 'boolean' ? path.visible : true
        };
      })
    : value.paths;

  return { ...value, zones, nodes, paths };
};

export const isLayoutPayload = (value: unknown): value is Partial<AppState> => {
  if (!isRecord(value)) return false;
  const nodes = value.nodes;
  const zones = value.zones;
  if (!Array.isArray(nodes) && !Array.isArray(zones)) return false;
  if (nodes !== undefined && (!Array.isArray(nodes) || !nodes.every(node =>
    isRecord(node) &&
    typeof node.id === 'string' &&
    isFiniteNumber(node.x) &&
    isFiniteNumber(node.y) &&
    typeof node.type === 'string' && nodeTypes.has(node.type) &&
    Array.isArray(node.connections) && node.connections.every(id => typeof id === 'string') &&
    isFiniteNumber(node.capacity) && Array.isArray(node.occupants) &&
    isFiniteNumber(node.contamination) && isFiniteNumber(node.processingTime) && isFiniteNumber(node.speedModifier)
  ))) return false;
  if (zones !== undefined && (!Array.isArray(zones) || !zones.every(zone =>
    isRecord(zone) &&
    typeof zone.id === 'string' &&
    typeof zone.type === 'string' && nodeTypes.has(zone.type) &&
    Array.isArray(zone.rects) && zone.rects.every(rect =>
      isRecord(rect) && isFiniteNumber(rect.x) && isFiniteNumber(rect.y) &&
      isFiniteNumber(rect.w) && isFiniteNumber(rect.h)
    )
  ))) return false;
  if (value.walls !== undefined && (!Array.isArray(value.walls) || !value.walls.every(wall =>
    isRecord(wall) && typeof wall.id === 'string' &&
    isFiniteNumber(wall.x1) && isFiniteNumber(wall.y1) && isFiniteNumber(wall.x2) && isFiniteNumber(wall.y2)
  ))) return false;
  if (value.paths !== undefined && (!Array.isArray(value.paths) || !value.paths.every(path =>
    isRecord(path) && typeof path.id === 'string' && typeof path.name === 'string' &&
    typeof path.color === 'string' && typeof path.visible === 'boolean' &&
    (path.kind === undefined || path.kind === 'SIMULATION' || path.kind === 'ARCHITECTURAL') &&
    (path.nodeIds === undefined || (Array.isArray(path.nodeIds) && path.nodeIds.every(id => id === null || typeof id === 'string'))) &&
    (path.generatedNodeIds === undefined || (Array.isArray(path.generatedNodeIds) && path.generatedNodeIds.every(id => typeof id === 'string'))) &&
    (path.connections === undefined || (Array.isArray(path.connections) && path.connections.every(edge => isRecord(edge) && typeof edge.fromId === 'string' && typeof edge.toId === 'string'))) &&
    Array.isArray(path.points) && path.points.every(point =>
      isRecord(point) && isFiniteNumber(point.x) && isFiniteNumber(point.y)
    )
  ))) return false;
  if (value.icons !== undefined && (!Array.isArray(value.icons) || !value.icons.every(icon =>
    isRecord(icon) && typeof icon.id === 'string' && isFiniteNumber(icon.x) && isFiniteNumber(icon.y) &&
    typeof icon.type === 'string' && iconTypes.has(icon.type)
  ))) return false;
  if (value.pixelsPerMeter !== undefined && (!isFiniteNumber(value.pixelsPerMeter) || value.pixelsPerMeter <= 0)) return false;
  if (value.floorPlanImage !== undefined && value.floorPlanImage !== null && typeof value.floorPlanImage !== 'string') return false;
  if (value.simConfig !== undefined && !isSimulationConfig(value.simConfig)) return false;
  return ['mapOpacity', 'mapBrightness', 'mapContrast'].every(key =>
    value[key] === undefined || isFiniteNumber(value[key])
  );
};
